import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../../api/cliente';
import { Boton } from './basicos';
import { useTextoDeError } from './estados';

/**
 * Anular un documento, con motivo obligatorio.
 *
 * Es el mismo gesto en cuatro lugares —recibo, orden de pago, recepción y factura— y por eso
 * vive una sola vez: cuatro copias del mismo diálogo se separan a la primera corrección.
 *
 * **El motivo no es un trámite.** Un documento anulado sin explicación es, tres meses después,
 * un agujero que nadie puede justificar ante quien pregunte.
 */
export function BotonDeAnulacion({
  ruta,
  onAnulado,
  etiqueta,
}: {
  readonly ruta: string;
  readonly onAnulado: () => Promise<void> | void;
  readonly etiqueta?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const textoDeError = useTextoDeError();

  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const anular = useMutation({
    mutationFn: () => pedir<unknown>(ruta, { method: 'POST', cuerpo: { motivo: motivo.trim() } }),
    onSuccess: async () => {
      setAbierto(false);
      setMotivo('');
      setError(null);
      await onAnulado();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (!abierto) {
    return (
      <Boton
        tono="secundario"
        onClick={() => {
          setAbierto(true);
          setError(null);
        }}
      >
        {etiqueta ?? t('cuentas.anular')}
      </Boton>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        aria-label={t('cuentas.motivoDeAnulacion')}
        placeholder={t('cuentas.motivoDeAnulacion')}
        maxLength={200}
        className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm"
        value={motivo}
        onChange={(evento) => {
          setMotivo(evento.target.value);
        }}
      />
      <div className="flex gap-2">
        <Boton
          tono="peligro"
          cargando={anular.isPending}
          disabled={motivo.trim() === ''}
          onClick={() => {
            anular.mutate();
          }}
        >
          {t('cuentas.confirmarAnulacion')}
        </Boton>
        <Boton
          tono="secundario"
          onClick={() => {
            setAbierto(false);
            setMotivo('');
          }}
        >
          {t('acciones.cancelar')}
        </Boton>
      </div>
      {error === null ? null : (
        <p role="alert" className="text-xs text-mal">
          {error}
        </p>
      )}
    </div>
  );
}
